import { type MouseEvent, type ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, type LinkProps, useLocation } from 'react-router-dom';
import { useSuppressedFilterFromRoot } from '../../state/FilterContext';
import { useSiteContext } from '../../state/SiteContext';
import { TextExtractor, findFirstComponentOfType } from '../internal/TextExtractor';
import { FilterState } from '../../state/state-models';
import { applyDefaults, getStateAsLocationQuery } from '../../state/state-url-management';
import { joinPathSearchAndHash } from '../../shared/utils/filter-redirect-url.utils';
import { isValidLayoutName } from '../../shared/utils/basefile.utils';
import { normalizeSitePath } from '../../shared/utils/book-edition-key.utils';
import { useBookPath, useBookSerializeToUrl } from '../../state/BookContext';
import { useBookOutletScope } from '../../state/BookOutletScopeContext';
import { InternalStateContext } from '../../state/InternalContext';
import { useLinkController } from '../../state/link-controller.context';
import {
    mergeLinkVerifierClassName,
    useLinkVerifier,
    type LinkVerifierStatus,
} from '../../state/link-verifier.context';
import { useToggleSwitchController } from '../../state/toggle-switch-controller.context';
import { activateToggleSwitchByName } from './toggle-switch-activation.utils';

export type AppLinkProps = {
  children: ReactNode;
  suppress?: string;
  layout?: string;
  activateToggleSwitch?: string;
  /**
   * When set (e.g. managed internal anchor from the content parser), this path is used as the
   * base destination. Current filter state and book param are merged into the URL (respecting
   * `suppress`), identical to authored `AppLink` usage. No layout-segment injection is applied
   * because parser-created paths are already fully qualified.
   * Authoring continues to use `arg-to` / `arg-display` when this is omitted.
   */
  to?: LinkProps['to'];
} & Omit<LinkProps, 'to' | 'children'>;

/***
 * A component that renders a link within the app.
 */
function getCurrentLayoutSegment(pathname: string, sitePath: string): string {
  const withoutSitePath = sitePath && pathname.startsWith(sitePath)
    ? pathname.substring(sitePath.length)
    : pathname;
  const [firstSegment] = withoutSitePath.split("/").filter(Boolean);
  if (!firstSegment?.startsWith("~")) {
    return "";
  }
  return firstSegment;
}

function getCurrentPathWithoutLayout(pathname: string, sitePath: string): string {
  const withoutSitePath = sitePath && pathname.startsWith(sitePath)
    ? pathname.substring(sitePath.length)
    : pathname;
  const segments = withoutSitePath.split("/").filter(Boolean);
  if (segments[0]?.startsWith("~")) {
    segments.shift();
  }
  return segments.length ? `/${segments.join("/")}` : "/";
}

function isExplicitParserDestination(to: LinkProps['to'] | undefined): to is string {
  return typeof to === 'string' && to.length > 0;
}

function isDownloadSet(download: LinkProps['download']): boolean {
  return download !== undefined && download !== false;
}

function resolveVerifierCandidate(
  destination: string | undefined,
  target: LinkProps['target'],
  download: LinkProps['download'],
  getStatus: ((path: string) => LinkVerifierStatus) | undefined,
  sitePath: string,
): { path: string; status: LinkVerifierStatus } | null {
  if (!getStatus || target === '_blank' || isDownloadSet(download)) {
    return null;
  }
  const normalized = normalizeSitePath(destination);
  if (!normalized) {
    return null;
  }
  // Parser `to` after fixInternalLinks is a router href (`/<site>/~<layout>/file`).
  // Exists lookup and okPaths use the stored metadata path (no site, no ~layout).
  const path = getCurrentPathWithoutLayout(normalized, sitePath);
  return { path, status: getStatus(path) };
}

function splitPathAndHash(destination: string): { path: string; hash: string } {
  const hashIdx = destination.indexOf('#');
  if (hashIdx < 0) {
    return { path: destination, hash: '' };
  }
  return {
    path: destination.substring(0, hashIdx),
    hash: destination.substring(hashIdx),
  };
}

let nextAppLinkInstanceId = 0;

function createAppLinkInstanceId(): string {
  nextAppLinkInstanceId += 1;
  return `applink-${nextAppLinkInstanceId}`;
}

const TRACKED_FILTER_KEYS = new Set(["a", "b", "c", "d", "e", "f", "g", "k", "l", "m", "n", "o", "s", "t", "v", "w", "book"]);

function mergeFilterParams(searchParams: URLSearchParams, filterParams: URLSearchParams): void {
  const staleKeys = Array.from(searchParams.keys()).filter(
    key => TRACKED_FILTER_KEYS.has(key) || key.startsWith("c.") || key.startsWith("d.") || key.startsWith("s.")
  );
  staleKeys.forEach(key => searchParams.delete(key));
  filterParams.forEach((value, key) => searchParams.set(key, value));
}

export function AppLink({
  children,
  suppress,
  layout,
  activateToggleSwitch,
  to: explicitToProp,
  onClick: userOnClick,
  ...linkPassthrough
}: AppLinkProps): ReactElement {
  const { siteState } = useSiteContext();
  const internalContext = useContext(InternalStateContext);
  const location = useLocation();
  const { inBookOutlet } = useBookOutletScope();
  const bookPath = useBookPath();
  const bookSerializeToUrl = useBookSerializeToUrl();
  const toggleSwitchController = useToggleSwitchController();
  const linkControllerDefaults = useLinkController();
  const linkVerifier = useLinkVerifier();
  const effectiveSuppress = suppress !== undefined ? suppress : linkControllerDefaults?.suppress;
  const effectiveLayout = layout !== undefined ? layout : linkControllerDefaults?.layout;
  const effectiveActivateToggleSwitch =
    activateToggleSwitch !== undefined ? activateToggleSwitch : linkControllerDefaults?.activateToggleSwitch;
  const linkedState: FilterState = useSuppressedFilterFromRoot(effectiveSuppress || "");
  if (siteState?.site) {
    applyDefaults(linkedState, siteState.site);
  }
  const effectiveSitePath = internalContext?.internalState?.isCustomDomain ? "" : (siteState?.sitePath || "");

  // Get to value from the to child
  const [toValue, setToValue] = useState("");
  const toChild = useMemo(() => findFirstComponentOfType(children, "arg-to"), [children]);
  const bookChild = useMemo(() => findFirstComponentOfType(children, "arg-book"), [children]);
  const [bookValue, setBookValue] = useState("");

  // Get the content to render in the link
  const linkDisplay = useMemo(() => findFirstComponentOfType(children, "arg-display"), [children]);

  const link = useMemo(() => {
    if (isExplicitParserDestination(explicitToProp)) {
      const { path: basePath, hash } = splitPathAndHash(explicitToProp);
      const searchParams = new URLSearchParams(location.search);
      mergeFilterParams(searchParams, getStateAsLocationQuery(linkedState));
      const resolvedBook = inBookOutlet && bookSerializeToUrl ? bookPath : "";
      if (resolvedBook) {
        searchParams.set("book", resolvedBook);
      }
      return joinPathSearchAndHash(basePath, searchParams.toString(), hash);
    }
    let layoutSegment = "";
    const currentPathWithoutLayout = getCurrentPathWithoutLayout(location.pathname, effectiveSitePath);
    const hasExplicitTo = !!toChild;
    const rawDestination = hasExplicitTo ? toValue : (effectiveLayout !== undefined ? currentPathWithoutLayout : toValue);
    const { path: pathFromTo, hash } = splitPathAndHash(rawDestination);
    // Hash-only arg-to (`#id`) keeps the current pathname; an empty path without a hash stays empty.
    const targetPath = pathFromTo || (hash ? currentPathWithoutLayout : pathFromTo);
    if (effectiveLayout === undefined) {
      layoutSegment = getCurrentLayoutSegment(location.pathname, effectiveSitePath);
    } else {
      const trimmedLayout = effectiveLayout.trim();
      if (trimmedLayout && isValidLayoutName(trimmedLayout)) {
        layoutSegment = `~${trimmedLayout}`;
      }
    }
    const searchParams = new URLSearchParams(location.search);
    mergeFilterParams(searchParams, getStateAsLocationQuery(linkedState));
    const resolvedBook = bookChild ? bookValue : (inBookOutlet && bookSerializeToUrl ? bookPath : "");
    if (resolvedBook) {
      searchParams.set("book", resolvedBook);
    }
    const hrefPath = layoutSegment
      ? `${effectiveSitePath}/${layoutSegment}${targetPath}`
      : `${effectiveSitePath}${targetPath}`;
    return joinPathSearchAndHash(hrefPath, searchParams.toString(), hash);
  }, [bookChild, bookPath, bookSerializeToUrl, bookValue, effectiveSitePath, explicitToProp, inBookOutlet, effectiveLayout, linkedState, location.pathname, location.search, toChild, toValue]);

  const applyActivateToggleSwitch = useCallback(() => {
    activateToggleSwitchByName(effectiveActivateToggleSwitch, toggleSwitchController, "AppLink");
  }, [effectiveActivateToggleSwitch, toggleSwitchController]);

  const handleLinkClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      userOnClick?.(event);
      applyActivateToggleSwitch();
    },
    [applyActivateToggleSwitch, userOnClick],
  );

  const verificationDestination = isExplicitParserDestination(explicitToProp) ? explicitToProp : toValue;
  const { className: authorClassName, ...restPassthrough } = linkPassthrough;
  const verifierCandidate = resolveVerifierCandidate(
    verificationDestination,
    linkPassthrough.target,
    linkPassthrough.download,
    linkVerifier?.getStatus,
    effectiveSitePath,
  );
  const mergedClassName = mergeLinkVerifierClassName(authorClassName, verifierCandidate?.status ?? null);

  const instanceIdRef = useRef("");
  if (!instanceIdRef.current) {
    instanceIdRef.current = createAppLinkInstanceId();
  }
  const instanceId = instanceIdRef.current;
  const registerVerifiedInstance = linkVerifier?.register;
  const unregisterVerifiedInstance = linkVerifier?.unregister;
  const verifiedPath = verifierCandidate?.path;
  // useEffect (not useLayoutEffect): register() call order still matches sibling markup
  // order, and renderToString stays free of useLayoutEffect SSR warnings.
  useEffect(() => {
    if (!registerVerifiedInstance || !unregisterVerifiedInstance) {
      return;
    }
    if (!verifiedPath) {
      unregisterVerifiedInstance(instanceId);
      return;
    }
    registerVerifiedInstance(instanceId, verifiedPath);
  }, [instanceId, registerVerifiedInstance, unregisterVerifiedInstance, verifiedPath]);
  useEffect(() => {
    if (!unregisterVerifiedInstance) {
      return;
    }
    return () => {
      unregisterVerifiedInstance(instanceId);
    };
  }, [instanceId, unregisterVerifiedInstance]);

  if (isExplicitParserDestination(explicitToProp)) {
    return (
      <Link to={link} onClick={handleLinkClick} {...restPassthrough} className={mergedClassName}>
        {children}
      </Link>
    );
  }

  return <>
    {toChild && (
      <TextExtractor onExtractedText={setToValue}>
        {toChild?.props.children}
      </TextExtractor>
    )}
    {bookChild && (
      <TextExtractor onExtractedText={setBookValue}>
        {bookChild?.props.children}
      </TextExtractor>
    )}
    <Link to={link} onClick={handleLinkClick} {...restPassthrough} className={mergedClassName}>{linkDisplay?.props.children}</Link>
  </>;
}